import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  Stack,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  IconButton,
  Collapse,
  LinearProgress,
  Table,
  TableBody,
  TableRow,
  TableCell,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  HelpOutline,
  LinkOff as LinkOffIcon,
  Psychology as PsychologyIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'sonner';
import { Link as RouterLink } from 'react-router-dom';
import RefreshButton from '../../components/common/RefreshButton';
import DisabledReason from '../../components/common/DisabledReason';
import ListToolbar from '../../components/common/ListToolbar';
import FilterPillRow from '../../components/common/FilterPillRow';
import EmptyState from '../../components/common/EmptyState';
import TableFooterSimple from '../../components/common/TableFooterSimple';
import useListQuery from '../../components/common/listQuery/useListQuery';
import CardListSkeleton from '../../Skeletons/CardListSkeleton';
import { SP } from '../../theme/tokens';
import { formatInt } from '../../utils/format';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY || '';
const ADMIN_HEADERS = { 'X-Admin-Key': ADMIN_KEY, 'Content-Type': 'application/json' };

const IGNORED_KEY = 'taxonomy_quality_ignored_ids';

/**
 * 🔴 `limit=1000` statt der stillen Servervorgabe 100 (Plan G14b §4.4).
 * 1000 ist die Analysegrenze des Endpunkts (`backend/routes/taxonomy_api.py:340`)
 * — die Zahl ist damit keine Erfindung des Aufrufers, sondern die Grenze der
 * Flaeche. Traegt die Antwort genau 1000 Befunde, ist der Bestand MOEGLICHERWEISE
 * groesser; dann steht „1000+ issues", nie eine geratene Zahl (§4.2, P10).
 */
const LIMIT = 1000;

/** Die Hausgroesse der Seite (§9.6), keine Nutzerwahl (§3.3: kein `pageSize`). */
const SEITENGROESSE = 50;

/** Die Schwere in der Reihenfolge, in der sortiert wird (Befund T6, P18). */
const SEVERITIES = [
  { value: 'critical', label: 'Critical' },
  { value: 'warning', label: 'Warning' },
  { value: 'info', label: 'Info' },
];

const SEVERITY_RANG = { critical: 0, warning: 1, info: 2 };

const TYPES = [
  { value: 'conflicting_signals', label: 'Conflicting Signals' },
  { value: 'missing_data', label: 'Missing Data' },
  { value: 'relation_gap', label: 'Relation Gaps' },
];

function loadIgnored() {
  try {
    return new Set(JSON.parse(localStorage.getItem(IGNORED_KEY) || '[]'));
  } catch {
    return new Set();
  }
}
function saveIgnored(set) {
  localStorage.setItem(IGNORED_KEY, JSON.stringify([...set]));
}

const severityConfig = {
  critical: { icon: <ErrorIcon />, color: 'error', label: 'Critical' },
  warning:  { icon: <WarningIcon />, color: 'warning', label: 'Warning' },
  info:     { icon: <InfoIcon />, color: 'info', label: 'Info' },
};

/**
 * Quality Inspector — der Listenreiter von `/taxonomy` (Plan G14b §2.4 Nr. 2).
 *
 * 🔴 Der Abfragezustand lebt in der ADRESSE (`q`, `quick`, `type`, `page`),
 * geschrieben von `useListQuery` (BS1). Der REITER (`?tab=quality`) gehoert dem
 * Hub, nicht dieser Liste: „Clear all" wird deshalb ueber `clear(je eigene
 * Dimension)` gekapselt (§2.4 Nr. 1) — `clearAll()` loescht `tab` von Bauart
 * wegen (`useListQuery.js:291`) und spraenge auf `overview`.
 *
 * 🔴 Die Serverfilter `severity`/`type` werden NICHT mehr gesendet: der ganze
 * Bestand (bis 1000) ist geladen, gefiltert wird im Browser (§4.1). Sonst
 * waeren Zaehler und Bestand zwei verschiedene Mengen.
 */
export default function QualityInspectorTab() {
  const theme = useTheme();
  // 🔴 R16-C4 (Nachbesserung 5, 2026-10-31): „Restore {n} hidden" sass in der
  // `refresh`-Gruppe von Z1. Auf 390 px hat die Gruppe `flexShrink: 0`, die
  // Suche `flex: 1` — der Knopf drueckte sie auf eine Lupe von rund 45 px.
  // Unter `sm` wandert der Knopf deshalb in eine eigene Zeile unter der Leiste
  // (er aendert den LISTENZUSTAND und muss ohne Oeffnen eines Blatts
  // erreichbar bleiben — das Filterblatt scheidet damit aus).
  const schmal = useMediaQuery(theme.breakpoints.down('sm'));
  // 🔴 `null` heisst „noch nie geladen", `[]` heisst „geladen und leer". Der
  // Unterschied ist der einzige Unterscheider zwischen Fehler- und Leerzustand.
  const [issues, setIssues] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [ignored, setIgnored] = useState(loadIgnored);
  // Migrated to sonner — showSnack routes to toast.* with the same call shape.
  const showSnack = useCallback((message, severity = 'success') => {
    const fn = severity === 'success' ? toast.success
      : severity === 'error' ? toast.error
      : severity === 'warning' ? toast.warning
      : toast.info;
    fn(message);
  }, []);

  const fetchIssues = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Nur `limit` — die beiden Filter sind Client-Sache (§4.1).
      const res = await axios.get(`${API_BASE_URL}/api/taxonomy/quality-issues`, {
        params: { limit: LIMIT },
      });
      setIssues(res.data.issues || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchIssues(); }, [fetchIssues]);

  const handleIgnore = useCallback((issueId) => {
    // 🔴 R16-W5 (Nachbesserung 3, 2026-10-31): der Toast zitierte „Restore
    // hidden" — diesen Knopf gibt es nicht; er heisst „Restore {n} hidden"
    // (`:467`). Mit der Zahl aus DEMSELBEN Vorgang steht der Name woertlich da.
    //
    // 🔴 R15-W8 / R16-W9 (Nachbesserung 4, 2026-10-31): `setIgnored(next)`
    // ersetzte den Zustand aus dem GERENDERTEN Stand — zwei Aufrufe im selben
    // Takt bauten beide auf demselben `ignored` und der zweite verlor den
    // ersten Eintrag. Wieder die funktionale Form; die Zahl fuer den Toast
    // kommt aus einer lokalen Variablen (`ignored.size + 1`), weil `setState`
    // den Updater nicht synchron ausfuehren muss.
    const naechste = ignored.size + 1;
    setIgnored((prev) => {
      const next = new Set(prev);
      next.add(issueId);
      return next;
    });
    // 🔴 R16-W1 (Nachbesserung 7, 2026-10-31): KEINE Ortsangabe mehr. „in the
    // toolbar" stimmte seit `bc773593` nur noch ab `sm` — unter `sm` steht
    // derselbe Knopf in einer eigenen Zeile UNTER der Leiste (R16-C4). Der
    // Text nennt den Knopf beim Namen (die Zahl aus DEMSELBEN Vorgang) und
    // traegt damit beide Formen, ohne eine Sonderregel je Breite.
    showSnack(`Issue hidden. Use “Restore ${naechste} hidden” to bring it back.`);
  }, [ignored.size, showSnack]);

  // Die Persistenz haengt am ZUSTAND, nicht am Handler: so landet auch der
  // zusammengesetzte Stand zweier Aufrufe im selben Takt in `localStorage`
  // (der Updater laeuft dann zweimal, geschrieben wird das Ergebnis).
  useEffect(() => { saveIgnored(ignored); }, [ignored]);

  const handleUnIgnoreAll = useCallback(() => {
    setIgnored(new Set());
  }, []);

  // ── Der Zustand der Liste lebt in der ADRESSE (Plan §3.1) ─────────────────
  // `q` ist Freitext (Suche), `quick` mehrwertig (Schwere), `type` einkwertig
  // (Issue Type, im Filter-Popover). Kein `sort`, kein `pageSize` (§3.3).
  const dimensionen = useMemo(() => [
    { key: 'q', kind: 'single', label: 'Search' },
    { key: 'quick', kind: 'quick', label: 'Severity', values: SEVERITIES },
    { key: 'type', kind: 'single', label: 'Issue Type', values: TYPES },
  ], []);
  const query = useListQuery({ dimensions: dimensionen });
  const { state, set, clear, chips, ignoredValues } = query;

  // 🔴 Die Kapsel (§2.4 Nr. 1): `tab` gehoert dem Hub. Je eigene Dimension ein
  // `clear` — der Hook setzt dabei `page` zurueck (`useListQuery.js:266`) und
  // schreibt `replace`, also ohne zweiten Verlaufseintrag.
  const panelRef = useRef(null);
  const clearAllMitFokus = useCallback(() => {
    clear('q');
    clear('quick');
    clear('type');
    // 🔴 Plan §5.3b Nr. 4 (Nachbesserung 3, 2026-10-31): nach „Clear all" steht
    // der Fokus auf dem ERSTEN Bedienelement von Z1 — hier das Suchfeld.
    // 🔴 R15-W6 (Nachbesserung 4, 2026-10-31): die frueher hier stehende
    // Begruendung („im Leerzustand gibt es keine Leiste") war falsch — im
    // `no-match` ist mindestens ein Filter aktiv (sonst waere der Grund
    // `no-data`), die `ActiveFilterBar` ist also sichtbar. Der Grund ist ein
    // anderer: dieser Knopf ruft `clearAllMitFokus` DIREKT (`:416`), nicht
    // ueber `ActiveFilterBar › allesZurueck`, und nur dort wird
    // `returnFocusRef` (`ActiveFilterBar.jsx:260`) gezogen. Hausform:
    // `DepHealth.jsx:287` und `LsmWatcher.jsx:511` setzen den Fokus
    // ausdruecklich.
    window.setTimeout(() => {
      panelRef.current?.querySelector('[role="searchbox"]')?.focus?.();
    }, 0);
  }, [clear]);

  // ── Ableitungen ───────────────────────────────────────────────────────────
  const basis = useMemo(() => issues ?? [], [issues]);

  // 🔴 Die Ignorier-Liste BLENDET AUS, sie filtert nicht (§3.3): sie erzeugt
  // keinen Chip, und `m` meint DIESELBE Menge wie die Schwere-Zaehler (§2.4
  // Nr. 5, Grill W12) — sonst wichen Σ(Zaehler) und `m` voneinander ab.
  const sichtbar = useMemo(
    () => basis.filter((i) => !ignored.has(i.id)),
    [basis, ignored],
  );

  const zaehler = useMemo(() => {
    const c = { critical: 0, warning: 0, info: 0 };
    for (const i of sichtbar) {
      if (c[i.severity] != null) c[i.severity] += 1;
    }
    return c;
  }, [sichtbar]);

  const gefiltert = useMemo(() => {
    const begriff = (state.q || '').trim().toLowerCase();
    const schnell = state.quick ?? [];
    return sichtbar.filter((i) => {
      if (schnell.length > 0 && !schnell.includes(i.severity)) return false;
      if (state.type && i.type !== state.type) return false;
      if (begriff) {
        // Hausumfang N7.6: `product_name` UND `sku` — beides steht sichtbar auf
        // der Karte (heute durchsuchte die Seite nur `product_name`).
        const name = (i.product_name || '').toLowerCase();
        const sku = (i.sku || '').toLowerCase();
        if (!name.includes(begriff) && !sku.includes(begriff)) return false;
      }
      return true;
    });
  }, [sichtbar, state.q, state.quick, state.type]);

  // Feste Sortierung nach Schwere (Befund T6, P18): innerhalb der Schwere
  // stabil nach dem Serverstand — deshalb der Index als zweites Kriterium.
  const sortiert = useMemo(() => {
    return gefiltert
      .map((issue, i) => ({ issue, i }))
      .sort((a, b) => (
        ((SEVERITY_RANG[a.issue.severity] ?? 9) - (SEVERITY_RANG[b.issue.severity] ?? 9))
        || (a.i - b.i)
      ))
      .map((x) => x.issue);
  }, [gefiltert]);

  const m = sichtbar.length;
  const n = sortiert.length;
  const seiten = Math.max(1, Math.ceil(n / SEITENGROESSE));
  const seite = Math.min(state.page ?? 1, seiten);
  const seitenZeilen = sortiert.slice((seite - 1) * SEITENGROESSE, seite * SEITENGROESSE);

  // 🔴 Der Server kappt bei `limit`; bleibt es bei genau 1000, ist der Bestand
  // MOEGLICHERWEISE groesser → „1000+ issues" (§4.2). Nur im ungefilterten
  // Zustand: „3+ issues" waere eine Behauptung ueber die Treffermenge.
  const gekappt = basis.length >= LIMIT;
  const unknownTotal = gekappt && n === m;

  const sperre = Boolean(error) && issues === null;

  const leerGrund = (() => {
    if (sperre) return 'error';
    if (issues === null) return null;
    if (basis.length === 0) return 'no-data';
    if (n === 0) return 'no-match';
    return null;
  })();

  // 🔴 Der Zustand „alle ausgeblendet" (Nachbesserung 7, 2026-10-31). Er ist
  // die EINE Lage, in der der Leerzustand selbst die Restore-Aktion traegt —
  // dort stehen Kappungs-Alert und die beiden Restore-Knoepfe (Leiste bzw.
  // Telefonzeile) zurueck:
  //  - R16-W3: der Kappungs-Alert nennt sonst die Suchgrenze als Grund, waehrend
  //    die Erklaerung darunter „All quality issues are hidden." sagt.
  //  - R16-W5: derselbe Knopf stand bis dahin zweimal auf dem Telefon (eigene
  //    Zeile UND Leerzustand) bzw. zweimal am Desktop (Leiste UND Leerzustand).
  //    Gleiche Handlung, gleicher Bildschirm — einmal reicht.
  const alleAusgeblendet = leerGrund === 'no-match' && m === 0 && ignored.size > 0;

  // ── Die Bausteine der Leiste ──────────────────────────────────────────────
  // 🔴 Die Zaehler sind der GRUNDBESTAND ohne Suche/Filter, OHNE die
  // ignorierten Befunde (§5.2/OE-4, §2.4 Nr. 1). Im FEHLER tragen sie keine
  // Zahl: eine 0 waere die Behauptung „nichts gefunden", wo „konnte nicht
  // laden" gilt (Regel 22 Nr. 3, P10).
  // 🔴 R16-W1 (Nachbesserung 3, 2026-10-31): bei gekapptem Bestand sind die
  // Schwere-Zaehler UNTERGRENZEN — der Gesamtwert daneben sagt „1.000+", die
  // Pillen sagten „445". Zwei Zaehler auf einem Bildschirm behaupteten damit
  // zwei verschiedene Dinge ueber dieselbe Unsicherheit. Das „+" macht die
  // Untergrenze sichtbar; es ist keine erfundene Zahl (Regel 22 Nr. 3).
  // 🔴 R16-W11 (Nachbesserung 4, 2026-10-31): das „+" bleibt AUCH bei 0 stehen
  // („Info 0+"). Eine nackte „0" waere wieder eine EXAKTE Zahl ueber den
  // gekappten Bestand — genau die Aussage, die R16-W1 abgestellt hat; hinter
  // der Grenze koennen Befunde dieser Stufe liegen. Der Preis ist, dass ein
  // Screenreader „null plus" liest; das ist ehrlicher als „keine".
  const quickOptionen = SEVERITIES.map((s) => ({
    ...s,
    ...(sperre ? {} : {
      count: gekappt ? `${formatInt(zaehler[s.value] || 0)}+` : (zaehler[s.value] || 0),
    }),
  }));

  const quickReihe = (
    <FilterPillRow
      multi
      alwaysShow
      options={quickOptionen}
      value={state.quick ?? []}
      onChange={(v) => set('quick', v)}
      disabled={sperre}
    />
  );

  const suchFeld = {
    value: state.q ?? '',
    onCommit: (v) => set('q', v || null),
    placeholder: 'Search products or SKUs…',
    label: 'Search products or SKUs',
    inputRole: 'searchbox',
    disabled: sperre,
    // 🔴 G14b-3 NB1 (2026-10-30): die Suche FUELLT die Leiste. Ohne diese Zeile
    // steht sie auf ihrer Eigenbreite — `SearchTrigger.jsx:439` setzt
    // `minWidth: 220`, und ein TextField waechst in einem Flex-Kasten nicht von
    // selbst. Gemessen am Zweig `g14b3a` (1440, P24): 220 px in einer 1112-px-
    // Leiste, also exakt der Eigenbreiten-Boden. Hausform: `KnShipments.jsx:384`,
    // `SecondLifeBestand.jsx:1967`, `IncomingInventory.jsx:1045`,
    // `POOverview.jsx:1249`, `VendorsOverview.jsx:548` reichen dieselbe Zeile.
    sx: { flex: 1, minWidth: 0 },
  };

  const filterBlatt = {
    label: 'Filters',
    title: 'Filters',
    // N4.6: die BLATT-Kriterien mit Wert, nicht `query.chips.length`.
    activeCount: state.type ? 1 : 0,
    criteria: [
      {
        key: 'type',
        label: 'Issue Type',
        value: state.type ?? '',
        onChange: (v) => set('type', v || null),
        options: TYPES,
        emptyLabel: 'Any issue type',
        disabled: sperre,
      },
    ],
    // P17: „Show N results" — die Trefferzahl, nicht der Bestand.
    resultCount: issues === null ? undefined : n,
    onClearAll: () => set('type', null),
    disabled: sperre,
  };

  const leerzustand = leerGrund ? (
    <Box sx={{ py: SP.lg }}>
      <EmptyState
        reason={leerGrund}
        message={
          leerGrund === 'error'
            ? 'Could not load quality issues.'
            : leerGrund === 'no-data'
              ? 'No quality issues found! Your taxonomy data is in excellent shape.'
              // 🔴 R15-W2 (Nachbesserung 9, 2026-11-01): EIN Praedikat, nicht zwei.
              // Hier stand `m === 0 && ignored.size > 0` als zweite Quelle neben
              // `alleAusgeblendet` (:309). Heute sind beide gleichwertig (der
              // `no-data`-Zweig darueber faengt den Fall mit leerem Bestand ab),
              // aber die naechste Aenderung an einer von beiden liesse Nachricht
              // und Knopf auseinanderlaufen.
              // 🔴 R16-W-NEW-2 (Nachbesserung 9, 2026-11-01): bei GEKAPPTEM Bestand
              // liegt hinter der Grenze moeglicherweise ein Befund — „all" waere
              // zu stark (der Zaehler traegt die Untergrenze nur als „0+"). Ohne
              // Kappung ist der geladene Bestand der ganze, dort bleibt der Satz.
              : alleAusgeblendet
                ? (gekappt
                  ? 'All loaded quality issues are hidden.'
                  : 'All quality issues are hidden.')
                : 'No issues match these filters.'
        }
        // P9 (no-match) verlangt ein Begriff-Echo, wenn eine Suche laeuft.
        // 🔴 R15-W2 (Nachbesserung 3, 2026-10-31): im GEFILTERTEN Zustand ist
        // `n !== m`, also schwieg `unknownTotal` — und der Leerzustand las sich
        // als „gibt es nicht" statt „in den ersten 1000 nicht".
        // 🔴 R16-C3 (Nachbesserung 4, 2026-10-31): der Kappungssatz stand hier
        // im `detail`-Slot, den `EmptyState` als `caption`/`text.disabled`
        // rendert (≈ 2,7 : 1 auf Weiss, WCAG 1.4.3 AA verlangt 4,5 : 1). Er
        // steht jetzt als eigener `Alert` ueber der Liste — und deshalb hier
        // NICHT mehr (keine Doppelung im Leerzustand).
        detail={
          leerGrund === 'error'
            ? error
            : leerGrund === 'no-match'
              ? (state.q ? `Nothing matches “${state.q}”.` : undefined)
              : undefined
        }
        action={
          leerGrund === 'no-match'
            ? (alleAusgeblendet
              ? <Button variant="outlined" onClick={handleUnIgnoreAll}>Restore {ignored.size} hidden</Button>
              : <Button variant="outlined" onClick={clearAllMitFokus}>Clear all filters</Button>)
            : undefined
        }
        onRetry={leerGrund === 'error' ? fetchIssues : undefined}
      />
    </Box>
  ) : null;

  return (
    <Box ref={panelRef} data-testid="liste-panel" aria-busy={loading || undefined} sx={{ py: { xs: 2, sm: 3 } }}>
      {/* Der GRUND der Sperre steht sichtbar ueber der Leiste (N8.5/P9).
          `role="none"`: der Leerzustand darunter ist bereits `role="alert"`. */}
      {sperre && (
        <Alert severity="error" role="none" sx={{ mb: SP.card }}>
          {error}
        </Alert>
      )}
      {!sperre && error && (
        <Alert severity="warning" sx={{ mb: SP.card }}>
          Could not refresh the list. The last loaded rows are still shown.
        </Alert>
      )}

      {/* 🔴 R16-C3 (Nachbesserung 4, 2026-10-31): der Kappungssatz macht den
          gefilterten Leerzustand ehrlich („in den ersten 1000 nicht" statt
          „gibt es nicht"). Im `detail`-Slot von `EmptyState` stand er in
          `text.disabled` (≈ 2,7 : 1) und war auf dem Foto kaum lesbar; als
          eigener `Alert` traegt er die Alarmfarbe und steht ueber der Liste —
          an dem Platz, an dem auch der Sperrgrund steht (`:428-432`). */}
      {!sperre && gekappt && leerGrund === 'no-match' && !alleAusgeblendet && (
        <Alert severity="info" data-testid="kappung-hinweis" sx={{ mb: SP.card }}>
          {`Only the first ${formatInt(LIMIT)} issues were searched.`}
        </Alert>
      )}

      <ListToolbar
        search={suchFeld}
        quickFilters={quickReihe}
        moreFilters={filterBlatt}
        count={issues === null ? undefined : {
          noun: 'issues',
          n,
          m,
          unknownTotal,
        }}
        refresh={(
          <Stack direction="row" spacing={SP.tight} alignItems="center">
            {/* Unter `sm` steht der Knopf in einer eigenen Zeile unter der
                Leiste (R16-C4) — hier wuerde er die Suche zusammendruecken. */}
            {!schmal && ignored.size > 0 && !alleAusgeblendet && (
              <Button size="small" variant="text" onClick={handleUnIgnoreAll}>
                Restore {ignored.size} hidden
              </Button>
            )}
            <Tooltip title="Show help information">
              <IconButton
                onClick={() => setShowHelp((v) => !v)}
                size="small"
                aria-label="Show help information"
              >
                <HelpOutline />
              </IconButton>
            </Tooltip>
            <RefreshButton
              ariaLabel="Refresh quality issues"
              loading={loading}
              firstLoad={loading && issues === null}
              onClick={fetchIssues}
            />
          </Stack>
        )}
        query={{ chips, clearAll: clearAllMitFokus }}
      >
        {/* R16-C4: die eigene Zeile fuer „Restore {n} hidden" unter `sm`. Die
            44-px-Hoehe traegt das Theme (`MuiButton` unter grobem Zeiger) —
            kein eigener Pixelwert hier (Regel 22). */}
        {schmal && ignored.size > 0 && !alleAusgeblendet && (
          <Box sx={{ mx: SP.card, mt: SP.tight }}>
            <Button
              data-testid="restore-hidden"
              variant="outlined"
              size="small"
              onClick={handleUnIgnoreAll}
            >
              Restore {ignored.size} hidden
            </Button>
          </Box>
        )}

        {ignoredValues.length > 0 && (
          <Alert severity="info" data-testid="ignoriert-hinweis" sx={{ mx: SP.card, mt: SP.tight }}>
            {`Ignored unknown filter value “${ignoredValues[0].value}”.`}
          </Alert>
        )}

        <Collapse in={showHelp}>
          <Alert severity="info" sx={{ mx: SP.card, mt: SP.tight }}>
            <Typography variant="subtitle2" gutterBottom>Understanding Quality Issues</Typography>
            <Box component="ul" sx={{ mt: 1, mb: 1, pl: 2 }}>
              <li><strong>Conflicting Signals:</strong> Taxonomy attributes point to a different classification than what&apos;s stored. Fix by applying the suggested override.</li>
              <li><strong>Missing Data:</strong> Required taxonomy fields are empty — affects recommendations, collections, and bundles. Set values directly here.</li>
              <li><strong>Relation Gaps:</strong> Product has no or very few computed relations, so it won&apos;t appear in recommendation widgets.</li>
            </Box>
            <Typography variant="body2">
              Use <strong>Ignore</strong> to hide false positives. They&apos;re stored in your browser and can be restored any time.
            </Typography>
          </Alert>
        </Collapse>

        {/* P18: es gibt keine Sortierwahl — der Satz ist der einzige Traeger.
            🔴 R16-W4 (Nachbesserung 3, 2026-10-31): er stand auch ueber Leer-
            und Fehlerzustaenden — dort gibt es nichts zu sortieren. */}
        {n > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid="sortiert-nach"
            sx={{ display: 'block', mx: SP.card, mt: SP.tight }}
          >
            Sorted by severity (critical first).
          </Typography>
        )}

        {issues === null && loading && (
          <Box data-testid="liste-skelett" sx={{ mx: SP.card, mt: SP.tight }}>
            <CardListSkeleton cards={4} lines={3} form="card" ariaLabel="Loading quality issues" />
          </Box>
        )}

        {leerzustand}

        {!leerzustand && (
          <Box data-testid="liste-karte" sx={{ mx: SP.card, mt: SP.tight }}>
            <Stack spacing={2}>
              {seitenZeilen.map((issue) => (
                <IssueCard
                  key={issue.id}
                  issue={issue}
                  onRefresh={fetchIssues}
                  onIgnore={handleIgnore}
                  showSnack={showSnack}
                />
              ))}
            </Stack>
          </Box>
        )}

        {/* N8.7: die Fusszeile traegt NUR mit Inhalt — der Seitenwechsel steht
            bei mehr als 50 Treffern, mobil im Fluss (`stickyOnMobile={false}`). */}
        {!leerzustand && n > 0 && (
          <Box data-testid="liste-fuss" sx={{ mx: SP.card }}>
            <TableFooterSimple
              count={n}
              page={seite - 1}
              onPageChange={(p) => set('page', p + 1)}
              rowsPerPage={SEITENGROESSE}
              stickyOnMobile={false}
            />
          </Box>
        )}
      </ListToolbar>
    </Box>
  );
}

// ─── Issue Card wrapper ────────────────────────────────────────────────────────

function IssueCard({ issue, onRefresh, onIgnore, showSnack }) {
  const config = severityConfig[issue.severity] || severityConfig.info;
  const theme = useTheme();
  // 🔴 R16-C5 (Nachbesserung 5, 2026-10-31): unter `sm` trug die Kopfzeile
  // drei Dinge nebeneinander (Schwere-Chip · Name · Typ-Chip). Auf 390 px
  // reichte die Breite nicht: der Name brach auf drei bis fünf Zeilen
  // („Fellow / Opus / Conical / Burr / Grinder"), und die Chips liefen ueber
  // die Kartenkante hinaus. Die Weiche ist dieselbe wie in `RefundsReport`
  // (Hausform `useMediaQuery(theme.breakpoints.down('sm'))`).
  const schmal = useMediaQuery(theme.breakpoints.down('sm'));

  const typeIcon = {
    conflicting_signals: <PsychologyIcon fontSize="small" />,
    relation_gap: <LinkOffIcon fontSize="small" />,
  }[issue.type] || null;

  // Die drei Teile des Kopfes, einmal beschrieben und zweimal gesetzt — so
  // koennen die zwei Formen nicht auseinanderlaufen.
  const schwereChip = (
    <Chip
      data-testid={`quality-schwere-${issue.id}`}
      icon={config.icon}
      label={config.label}
      color={config.color}
      size="small"
      // Nur unter `sm`: dort traegt die Zeile den Chip in voller Groesse.
      // Ab `sm` bleibt das Schrumpfverhalten der Bestandszeile unveraendert.
      sx={schmal ? { flexShrink: 0 } : undefined}
    />
  );
  const typChip = (
    <Chip
      data-testid={`quality-typ-${issue.id}`}
      icon={typeIcon}
      label={issue.type.replace(/_/g, ' ')}
      size="small"
      variant="outlined"
    />
  );
  // 🔴 R16-C1 (Nachbesserung 3, 2026-10-31): die Suche deckt `product_name`
  // UND `sku` (`:258-259`), die zugeklappte Karte zeigte aber nur den Namen.
  // Wer per SKU sucht, fand eine Zeile, in der der Suchgrund nicht stand. Die
  // SKU steht als zweite Zeile in der vorhandenen Texthierarchie (`caption`,
  // `text.secondary`) — kein eigener Tiefenlink, den traegt der Koerper.
  const titelBlock = (
    <Stack sx={{ flex: 1, minWidth: 0 }}>
      <Typography data-testid={`karte-titel-${issue.id}`} variant="subtitle1">
        {issue.product_name}
      </Typography>
      {issue.sku && (
        <Typography
          data-testid={`karte-sku-${issue.id}`}
          variant="caption"
          color="text.secondary"
          sx={{ overflowWrap: 'anywhere' }}
        >
          {issue.sku}
        </Typography>
      )}
    </Stack>
  );

  return (
    // 🔴 Die Marken sind die HAUSFORM der Karte: `karte-kennung-*` traegt den
    // Zeilenbezug (P2 liest sie, P20 zaehlt sie), `karte-titel-*` den Titel
    // (P18 liest die Reihenfolge der Schnittmenge daran, `helfer.ts:1823`).
    <Accordion data-testid={`karte-kennung-${issue.id}`}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        {schmal ? (
          // Telefonform: Zeile 1 Schwere links / Typ rechts (beide in voller
          // Groesse, nichts ragt ueber die Karte), Zeile 2 Name + SKU ueber die
          // volle Breite. Kein `pr` — der Abstand zum Aufklapper entsteht aus
          // dessen eigenem Platz, nicht aus Kartenbreite.
          <Stack data-testid={`karte-kopf-mobil-${issue.id}`} sx={{ width: '100%', minWidth: 0 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={SP.tight}
              sx={{ width: '100%', minWidth: 0 }}
            >
              {schwereChip}
              {typChip}
            </Stack>
            <Box sx={{ minWidth: 0, mt: SP.chip }}>{titelBlock}</Box>
          </Stack>
        ) : (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', pr: 2 }}>
            {/* P22: die Schwere traegt WORT und SYMBOL — nie Farbe allein. */}
            {schwereChip}
            {titelBlock}
            {typChip}
          </Stack>
        )}
      </AccordionSummary>
      <AccordionDetails>
        {issue.type === 'conflicting_signals' && (
          <ConflictingSignalsDetails issue={issue} onRefresh={onRefresh} onIgnore={onIgnore} showSnack={showSnack} />
        )}
        {issue.type === 'missing_data' && (
          <MissingDataDetails issue={issue} onRefresh={onRefresh} onIgnore={onIgnore} showSnack={showSnack} />
        )}
        {issue.type === 'relation_gap' && (
          <RelationGapDetails issue={issue} onIgnore={onIgnore} />
        )}
      </AccordionDetails>
    </Accordion>
  );
}
// ─── Conflicting Signals ───────────────────────────────────────────────────────

function ConflictingSignalsDetails({ issue, onRefresh, onIgnore, showSnack }) {
  const { details } = issue;
  const [saving, setSaving] = useState(false);

  const overrideAction = (issue.suggested_actions || []).find((a) => a.type === 'override');
  const overrideFields = overrideAction?.override_fields || {};

  const handleApplyOverride = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/admin/taxonomy/overrides`,
        { product_id: issue.product_id, ...overrideFields },
        { headers: ADMIN_HEADERS }
      );
      showSnack(`Override applied for "${issue.product_name}" — classified as ${details.suggested}`);
      onRefresh();
    } catch (err) {
      // `hinweis` vor `error` (D11/W3): der Code allein sagt dem Bediener nichts.
      const data = err?.response?.data;
      showSnack(`Failed to apply override: ${data?.hinweis || data?.error || err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        This product has conflicting signals for categorization. The system detected strong{' '}
        <strong>{details.suggested}</strong> signals but the product is currently classified as{' '}
        <strong>{details.current}</strong>.
      </Typography>

      {/* Signal Analysis */}
      <Paper variant="outlined" sx={{ p: 2, my: 2, bgcolor: 'background.default' }}>
        <Typography variant="subtitle2" gutterBottom>Signal Analysis</Typography>
        {Object.entries(details.signals || {}).map(([useCase, signals]) =>
          signals.length > 0 ? (
            <Box key={useCase} sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold" sx={{ textTransform: 'capitalize', mb: 1 }}>
                {useCase} signals ({signals.length}):
              </Typography>
              <List dense disablePadding>
                {signals.map((signal, idx) => (
                  <ListItem key={idx} sx={{ py: 0 }}>
                    <ListItemText
                      primary={`${signal.source} = "${signal.value}"`}
                      secondary={`Weight: +${signal.weight}`}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : null
        )}

        {details.blockers?.length > 0 && (
          <Box>
            <Typography variant="body2" fontWeight="bold" color="error" gutterBottom>Blockers:</Typography>
            <List dense disablePadding>
              {details.blockers.map((blocker, idx) => (
                <ListItem key={idx} sx={{ py: 0 }}>
                  <ListItemText
                    primary={blocker.reason}
                    secondary={`Source: ${blocker.source} (weight: -${blocker.weight})`}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Paper>

      {/* Score summary */}
      <Paper variant="outlined" sx={{ p: 2, my: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
          <Box>
            <Typography variant="body2" color="text.secondary">Current</Typography>
            <Chip label={details.current} />
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Suggested</Typography>
            <Chip label={details.suggested} color="success" />
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Confidence</Typography>
            <Typography variant="h6" color="success.main">
              {Math.round((details.confidence || 0) * 100)}%
            </Typography>
          </Box>
          {details.threshold_info && (
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Signal score: {details.threshold_info.current_score} / {details.threshold_info.required_score} threshold
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, (details.threshold_info.current_score / details.threshold_info.required_score) * 100)}
                color={details.threshold_info.current_score >= details.threshold_info.required_score ? 'success' : 'warning'}
              />
            </Box>
          )}
        </Stack>
      </Paper>

      {/* Override preview */}
      {Object.keys(overrideFields).length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, my: 2, borderColor: 'success.light' }}>
          <Typography variant="subtitle2" gutterBottom>Override will set:</Typography>
          <Table size="small">
            <TableBody>
              {Object.entries(overrideFields).map(([field, value]) => (
                <TableRow key={field}>
                  <TableCell sx={{ fontFamily: 'monospace', py: 0.5 }}>{field}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    {Array.isArray(value) ? value.join(', ') : String(value)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <DisabledReason
          reason={saving
            ? null
            : Object.keys(overrideFields).length === 0 ? 'Enter the override values first.' : null}
        >
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={handleApplyOverride}
            disabled={saving || Object.keys(overrideFields).length === 0}
          >
            {saving ? 'Applying…' : `Apply override → ${details.suggested}`}
          </Button>
        </DisabledReason>
        <Button variant="text" size="small" color="inherit" onClick={() => onIgnore(issue.id)}>
          Ignore
        </Button>
      </Stack>
    </Box>
  );
}

// ─── Missing Data ──────────────────────────────────────────────────────────────

function MissingDataDetails({ issue, onRefresh, onIgnore, showSnack }) {
  const { details } = issue;
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const allowedValues = details.allowed_values || null;

  const handleSave = async () => {
    if (!value.trim()) return;
    setSaving(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/admin/taxonomy/overrides`,
        { product_id: issue.product_id, [details.field]: value },
        { headers: ADMIN_HEADERS }
      );
      showSnack(`Set ${details.field} = "${value}" for "${issue.product_name}"`);
      setOpen(false);
      setValue('');
      onRefresh();
    } catch (err) {
      // Zweiter catch derselben Datei (D11/W3) — dieselbe Ableitung.
      const data = err?.response?.data;
      showSnack(`Failed: ${data?.hinweis || data?.error || err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        This product is missing required taxonomy data:
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, my: 2, bgcolor: 'background.default' }}>
        <Typography variant="body2" fontWeight="bold" gutterBottom>
          Missing field: <code>{details.field}</code>
        </Typography>
        <Typography variant="body2" color="text.secondary">{details.message}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Impact: {details.impact}
        </Typography>
      </Paper>

      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Button variant="contained" size="small" onClick={() => setOpen((o) => !o)}>
          {open ? 'Cancel' : `Set ${details.field}`}
        </Button>
        <Button variant="text" size="small" color="inherit" onClick={() => onIgnore(issue.id)}>
          Ignore
        </Button>
      </Stack>

      <Collapse in={open}>
        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
          {allowedValues ? (
            <FormControl fullWidth size="small">
              <InputLabel>{details.field}</InputLabel>
              <Select value={value} onChange={(e) => setValue(e.target.value)} label={details.field}>
                {allowedValues.map((v) => (
                  <MenuItem key={v} value={v}>{v}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              fullWidth
              size="small"
              label={details.field}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={`Enter ${details.field}…`}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
            />
          )}
          <DisabledReason
            reason={saving ? null : !value.trim() ? 'Enter a value first.' : null}
          >
            <Button
              variant="contained"
              size="small"
              sx={{ mt: 1.5 }}
              onClick={handleSave}
              disabled={saving || !value.trim()}
            >
              {saving ? 'Saving…' : 'Save override'}
            </Button>
          </DisabledReason>
        </Paper>
      </Collapse>
    </Box>
  );
}

// ─── Relation Gap ──────────────────────────────────────────────────────────────

function RelationGapDetails({ issue, onIgnore }) {
  const { details } = issue;
  const isNone = details.gap_type === 'no_relations';

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {details.message}
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, my: 2, bgcolor: 'background.default' }}>
        <Stack spacing={1}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinkOffIcon color={isNone ? 'error' : 'warning'} />
            <Typography variant="body2" fontWeight="bold">
              {isNone ? 'No relations found' : `Only ${details.relation_count} relation(s)`}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">{details.impact}</Typography>
          {(issue.suggested_actions || []).map((action, i) => (
            action.type === 'fix_taxonomy' && (
              <Alert key={i} severity="info" sx={{ mt: 1 }}>
                <strong>Fix:</strong> {action.reason}
              </Alert>
            )
          ))}
        </Stack>
      </Paper>

      <Stack direction="row" spacing={2}>
        <Button
          variant="outlined"
          size="small"
          component={issue.sku ? RouterLink : 'button'}
          to={issue.sku ? `/products/${encodeURIComponent(issue.sku)}` : undefined}
          disabled={!issue.sku}
          target="_blank"
          rel="noopener"
        >
          Open product
        </Button>
        <Button variant="text" size="small" color="inherit" onClick={() => onIgnore(issue.id)}>
          Ignore
        </Button>
      </Stack>
    </Box>
  );
}
